import {
  type ModalOwnProps as MuiModalOwnProps,
  type PolymorphicProps,
} from '@mui/base'
import { clsx } from 'clsx'
import {
  type ComponentProps,
  type ElementType,
  type JSX,
  type ReactNode,
} from 'react'

import { Modal } from '../Modal'

type DialogOwnProps = Omit<
  MuiModalOwnProps,
  'children' | 'slotProps' | 'slots'
> & {
  children?: ReactNode
  slotProps?: MuiModalOwnProps['slotProps'] & {
    dialog?: ComponentProps<'div'>
  }
  slots?: MuiModalOwnProps['slots'] & {
    dialog?: ElementType
  }
}

interface DialogTypeMap<RootComponentType extends ElementType = 'div'> {
  defaultComponent: RootComponentType
  props: DialogOwnProps
}

export type DialogProps<
  RootComponentType extends ElementType = DialogTypeMap['defaultComponent'],
> = PolymorphicProps<DialogTypeMap<RootComponentType>, RootComponentType>

/**
 * Dialogs inform users about a task and can contain critical information, require decisions, or involve multiple tasks.
 *
 * The HTML `<dialog>` element can be used to build dialogs.
 * However, it is not yet widely supported across browsers,
 * and building fully accessible custom dialogs from scratch is very difficult and error prone.
 * Dialog helps achieve accessible dialogs that can be styled as needed.
 */
export const Dialog = <RootComponentType extends ElementType = 'div'>(
  props: DialogProps<RootComponentType>,
): JSX.Element => {
  const { children, slotProps, slots, ...modalProps } = props

  const DialogComponent = slots?.dialog ?? 'div'

  return (
    <Modal slotProps={slotProps} slots={slots} {...modalProps}>
      <DialogComponent
        {...slotProps?.dialog}
        aria-modal="true"
        className={clsx('dialog', slotProps?.dialog?.className)}
        role="dialog"
      >
        {children}
      </DialogComponent>
    </Modal>
  )
}
